import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Sparkles, ArrowRight, Download, Package, ShoppingBag, Lightbulb, ThumbsUp, Send, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useSiteContent } from '../utils/useSiteContent';

export default function ToolsShop() {
  const t = useSiteContent('tools.');
  const [topics, setTopics] = useState([]);
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [sug, setSug] = useState({ title: '', description: '', price_cents: 500, submitter_name: '', submitter_email: '' });
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth();
  const isMember = user?.role === 'admin' || user?.sanctuary_status === 'active';

  useEffect(() => {
    Promise.all([
      api.get('/tools/topics').then(r => r.data).catch(() => []),
      api.get('/tools').then(r => r.data).catch(() => []),
      api.get('/tool-suggestions').then(r => r.data).catch(() => []),
    ]).then(([t, tls, sgs]) => {
      setTopics(t);
      setTools(tls);
      setSuggestions(sgs);
      setLoading(false);
    });
  }, []);

  const submitSuggestion = async (e) => {
    e.preventDefault();
    if (!sug.title.trim()) return toast.error('Add a title');
    if (!sug.description.trim()) return toast.error('Add a short description');
    if (!user && !sug.submitter_email.trim()) return toast.error("Add your email so Genesis can reach out if she picks yours");
    setSubmitting(true);
    try {
      const body = {
        title: sug.title.trim(),
        description: sug.description.trim(),
        suggested_price_cents: sug.price_cents,
      };
      if (!user) {
        body.submitter_name = sug.submitter_name.trim();
        body.submitter_email = sug.submitter_email.trim();
      }
      const { data } = await api.post('/tool-suggestions', body);
      setSuggestions([data, ...suggestions]);
      setSug({ title: '', description: '', price_cents: 500, submitter_name: '', submitter_email: '' });
      setShowSuggest(false);
      toast.success('Thank you — your suggestion is in.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit');
    } finally { setSubmitting(false); }
  };

  const upvoteSuggestion = async (sid) => {
    if (!user) { toast.error('Please log in to upvote'); return; }
    try {
      const { data } = await api.post(`/tool-suggestions/${sid}/upvote`);
      setSuggestions(suggestions.map(x => x.id === sid ? { ...x, i_upvoted: data.i_upvoted, upvote_count: data.upvote_count } : x));
    } catch (_) { toast.error('Could not update'); }
  };

  const toolsByTopic = topics.map(topic => ({
    topic,
    items: tools.filter(t => t.topic_id === topic.id),
  })).filter(g => g.items.length > 0);

  return (
    <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1490730141103-6cac27aaab94?crop=entropy&cs=srgb&fm=jpg&w=1920&q=85)', backgroundSize: 'cover', backgroundPosition: 'center', filter: 'saturate(0.9) brightness(1.1)' }} aria-hidden />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(253, 251, 247, 0.6) 0%, rgba(253, 251, 247, 0.85) 60%, rgba(253, 251, 247, 0.98) 100%)' }} aria-hidden />
        <div className="max-w-5xl mx-auto px-6 pt-20 pb-14 relative text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{ backgroundColor: '#78A18E25', border: '1px solid #78A18E60' }}>
            <Package className="w-4 h-4" style={{ color: '#5C715E' }} />
            <span className="text-sm" style={{ fontFamily: 'Manrope, sans-serif', color: '#3F5641', fontWeight: 600 }}>{t('hero.pill', 'Sacred Tools')}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29', lineHeight: 1.15 }} data-testid="shop-hero-title">
            {t('hero.title', 'Tools for your relationships')}
          </h1>
          <p className="text-lg max-w-2xl mx-auto" style={{ fontFamily: 'Lora, serif', color: '#4A423B', fontStyle: 'italic' }}>
            {t('hero.subtitle', 'Workbooks, scripture cards, and practical guides you can bring straight into your marriage, family, and friendships.')}
          </p>
          {isMember && (
            <p className="mt-4 text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#5C715E', fontWeight: 600 }} data-testid="shop-member-perk">
              {t('hero.member_perk', 'Sanctuary members save on every tool — your discount is auto-applied at checkout.')}
            </p>
          )}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-16">
        {loading ? (
          <div className="text-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 mx-auto" style={{ borderColor: '#E57A44' }} /></div>
        ) : toolsByTopic.length === 0 ? (
          <div className="p-14 rounded-3xl text-center max-w-xl mx-auto" style={{ backgroundColor: '#FFFFFF', border: '1px dashed #E6E2D8' }} data-testid="shop-empty">
            <ShoppingBag className="w-12 h-12 mx-auto mb-4" style={{ color: '#E8C265' }} />
            <p className="text-lg mb-2" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29', fontWeight: 600 }}>{t('empty.title', 'Tools are on their way')}</p>
            <p style={{ fontFamily: 'Lora, serif', color: '#595553', fontStyle: 'italic' }}>{t('empty.body', 'Genesis is preparing the first collection. Check back soon.')}</p>
          </div>
        ) : (
          <div className="space-y-16">
            {toolsByTopic.map(({ topic, items }) => (
              <div key={topic.id} data-testid={`shop-topic-${topic.id}`}>
                <div className="flex items-end gap-4 mb-6 flex-wrap">
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${topic.tint}20`, color: topic.tint }}>
                    <Sparkles className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{topic.name}</h2>
                    {topic.description && <p className="text-sm mt-1" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>{topic.description}</p>}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {items.map(t => (
                    <ToolCard key={t.id} tool={t} isMember={isMember} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {user && (
          <div className="mt-16 text-center">
            <Link to="/my-tools" className="inline-flex items-center gap-2 text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#E57A44', fontWeight: 600 }} data-testid="shop-goto-mytools">
              <Download className="w-4 h-4" /> View my toolkit →
            </Link>
          </div>
        )}
      </section>

      {/* Suggestion box */}
      <section id="suggest-a-tool" className="max-w-5xl mx-auto px-6 pb-24">
        <div className="p-8 rounded-3xl relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #FFFFFF 0%, #F0F5FA 100%)', border: '1px solid #5B7C9930', boxShadow: '0 12px 40px rgba(0,0,0,0.04)' }} data-testid="shop-suggest-panel">
          <div className="flex items-start gap-4 mb-5">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#5B7C9925', color: '#5B7C99' }}>
              <Lightbulb className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{t('suggest.title', 'Suggest a tool')}</h2>
              <p style={{ fontFamily: 'Nunito, sans-serif', color: '#595553', lineHeight: 1.7 }}>
                {t('suggest.body', "Is there a workbook, printable, or scripture-anchored guide you wish existed? Tell Genesis what you'd want, and what feels fair to pay for it ($2 – $10). She'll create it if it resonates with what others are asking for too.")}
              </p>
            </div>
          </div>

          {!showSuggest ? (
            <button onClick={() => setShowSuggest(true)} className="px-6 py-3 rounded-full flex items-center gap-2 transition hover:-translate-y-0.5"
              style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#5B7C99', color: '#FFFFFF', fontWeight: 700 }}
              data-testid="shop-suggest-open">
              <Lightbulb className="w-4 h-4" /> Share your idea
            </button>
          ) : (
            <form onSubmit={submitSuggestion} className="grid grid-cols-1 gap-4 mt-2" data-testid="shop-suggest-form">
              <div>
                <label className="block mb-2 text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2724' }}>What tool would help you?</label>
                <input value={sug.title} onChange={(e) => setSug({ ...sug, title: e.target.value })} maxLength={180} required
                  placeholder="e.g., A weekly marriage check-in worksheet"
                  className="w-full px-4 py-3 rounded-xl border" style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#FDFBF7', borderColor: '#E6E2D8', color: '#2C2724' }}
                  data-testid="shop-suggest-title" />
              </div>
              <div>
                <label className="block mb-2 text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2724' }}>How would you use it?</label>
                <textarea value={sug.description} onChange={(e) => setSug({ ...sug, description: e.target.value })} maxLength={2000} required rows={4}
                  placeholder="A few sentences — what's inside, who it's for, what season you're in"
                  className="w-full px-4 py-3 rounded-xl border" style={{ fontFamily: 'Lora, serif', backgroundColor: '#FDFBF7', borderColor: '#E6E2D8', color: '#2C2724' }}
                  data-testid="shop-suggest-description" />
              </div>
              <div>
                <label className="block mb-2 text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2724' }}>
                  What feels fair to pay? <span className="ml-2 text-lg" style={{ color: '#E57A44', fontWeight: 700 }}>${(sug.price_cents / 100).toFixed(2)}</span>
                </label>
                <input type="range" min="200" max="1000" step="50" value={sug.price_cents} onChange={(e) => setSug({ ...sug, price_cents: parseInt(e.target.value, 10) })}
                  className="w-full" style={{ accentColor: '#E57A44' }} data-testid="shop-suggest-price" />
                <div className="flex justify-between text-xs mt-1" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>
                  <span>$2</span><span>$5</span><span>$10 max</span>
                </div>
              </div>
              {!user && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block mb-2 text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2724' }}>Your name <span className="text-xs" style={{ color: '#8C867D', fontWeight: 400 }}>(optional)</span></label>
                    <input value={sug.submitter_name} onChange={(e) => setSug({ ...sug, submitter_name: e.target.value })} maxLength={120}
                      className="w-full px-4 py-3 rounded-xl border" style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#FDFBF7', borderColor: '#E6E2D8', color: '#2C2724' }} />
                  </div>
                  <div>
                    <label className="block mb-2 text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2724' }}>Your email <span className="text-xs" style={{ color: '#8C867D', fontWeight: 400 }}>(only visible to Genesis)</span></label>
                    <input type="email" value={sug.submitter_email} onChange={(e) => setSug({ ...sug, submitter_email: e.target.value })} maxLength={200} required
                      className="w-full px-4 py-3 rounded-xl border" style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#FDFBF7', borderColor: '#E6E2D8', color: '#2C2724' }} />
                  </div>
                </div>
              )}
              <div className="flex gap-3 flex-wrap">
                <button type="submit" disabled={submitting} className="px-6 py-3 rounded-full flex items-center gap-2 transition hover:-translate-y-0.5 disabled:opacity-50"
                  style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#5B7C99', color: '#FFFFFF', fontWeight: 700 }}
                  data-testid="shop-suggest-submit">
                  <Send className="w-4 h-4" /> {submitting ? 'Submitting…' : 'Submit suggestion'}
                </button>
                <button type="button" onClick={() => setShowSuggest(false)} className="px-4 py-2 rounded-full text-sm" style={{ fontFamily: 'Manrope, sans-serif', color: '#595553', fontWeight: 600 }}>Cancel</button>
              </div>
            </form>
          )}
        </div>

        {suggestions.length > 0 && (
          <div className="mt-10">
            <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Ideas from the community</h3>
            <p className="text-sm mb-6" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>Add a heart to the ones that resonate — Genesis prioritizes the ones many of you want.</p>
            <ul className="space-y-3">
              {suggestions.map(s => (
                <li key={s.id} className="p-5 rounded-2xl flex items-start gap-4" style={{ backgroundColor: '#FFFFFF', border: '1px solid #F0EBE0' }} data-testid={`suggestion-${s.id}`}>
                  <button onClick={() => upvoteSuggestion(s.id)} className="flex flex-col items-center gap-1 px-3 py-2 rounded-xl transition hover:-translate-y-0.5"
                    style={{ backgroundColor: s.i_upvoted ? '#5B7C99' : '#F0F5FA', color: s.i_upvoted ? '#FFFFFF' : '#5B7C99', minWidth: '52px' }}
                    data-testid={`suggestion-upvote-${s.id}`}>
                    <ThumbsUp className="w-4 h-4" fill={s.i_upvoted ? '#FFFFFF' : 'none'} />
                    <span className="text-xs font-bold">{s.upvote_count}</span>
                  </button>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h4 className="text-base font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{s.title}</h4>
                      <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: statusColor(s.status).bg, color: statusColor(s.status).fg, fontFamily: 'Manrope, sans-serif', fontWeight: 700 }}>{statusLabel(s.status)}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#FDFBF7', border: '1px solid #E6E2D8', color: '#595553', fontFamily: 'Nunito, sans-serif' }}>${(s.suggested_price_cents / 100).toFixed(2)}</span>
                    </div>
                    <p className="text-sm mb-1" style={{ fontFamily: 'Lora, serif', color: '#595553', lineHeight: 1.7 }}>{s.description}</p>
                    <p className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>{s.submitter_name} · {new Date(s.created_at).toLocaleDateString()}</p>
                    {s.created_tool_id && (
                      <Link to={`/tools/${s.created_tool_id}`} className="mt-2 inline-flex items-center gap-1 text-xs" style={{ color: '#78A18E', fontWeight: 700, fontFamily: 'Nunito, sans-serif' }}>
                        <Check className="w-3 h-3" /> Now in the shop →
                      </Link>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}

function ToolCard({ tool, isMember }) {
  const showMember = isMember && tool.member_price_cents && tool.member_price_cents < tool.price_cents;
  const effective = showMember ? tool.member_price_cents : tool.price_cents;
  return (
    <Link to={`/tools/${tool.id}`} className="block rounded-3xl overflow-hidden transition-all duration-300 hover:-translate-y-2"
      style={{ backgroundColor: '#FFFFFF', boxShadow: '0 8px 30px rgba(0,0,0,0.04)', border: '1px solid #F0EBE0' }}
      data-testid={`shop-tool-card-${tool.id}`}>
      <div className="h-40" style={{ background: tool.cover_url ? `url(${tool.cover_url}) center/cover` : `linear-gradient(135deg, ${tool.topic?.tint || '#E57A44'}30 0%, ${tool.topic?.tint || '#E57A44'}10 100%)` }}>
        {!tool.cover_url && (
          <div className="h-full flex items-center justify-center">
            <Package className="w-12 h-12" style={{ color: tool.topic?.tint || '#E57A44', opacity: 0.55 }} />
          </div>
        )}
      </div>
      <div className="p-5">
        <h3 className="text-lg font-bold mb-2" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29', lineHeight: 1.3 }}>{tool.title}</h3>
        <p className="text-sm mb-4 line-clamp-3" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553', lineHeight: 1.6 }}>{tool.description}</p>
        <div className="flex items-center justify-between">
          <div>
            {showMember && <div className="text-xs line-through" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>${(tool.price_cents / 100).toFixed(2)}</div>}
            <div className="text-xl font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#E57A44' }}>${(effective / 100).toFixed(2)}</div>
          </div>
          <ArrowRight className="w-5 h-5" style={{ color: '#E57A44' }} />
        </div>
      </div>
    </Link>
  );
}

const statusColor = (s) => ({
  new: { bg: '#5B7C9920', fg: '#5B7C99' },
  considering: { bg: '#E8C26525', fg: '#B38A2E' },
  created: { bg: '#78A18E25', fg: '#5C715E' },
  declined: { bg: '#F3EFE7', fg: '#8C867D' },
}[s] || { bg: '#F3EFE7', fg: '#8C867D' });

const statusLabel = (s) => ({
  new: 'New idea',
  considering: 'Considering',
  created: 'Created ✓',
  declined: 'Not this one',
}[s] || s);

